import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialogModule } from '@angular/material/dialog';
import { AssistantConversationService } from '../../chatbot/services/assistant-conversation.service';
import { AppConfirmService } from '../../app-confirm/app-confirm.service';
import { ConversationSummary } from '../../chatbot/models/rag.models';

interface ConversationGroup {
  label: string;
  items: ConversationSummary[];
}

const DAY_MS = 24 * 60 * 60 * 1000;
const PAGE_SIZE = 50;

/**
 * ConversationListComponent — sidebar de historial del Asistente Virtual.
 *
 * Pide una sola página de hasta PAGE_SIZE conversaciones y las agrupa por
 * fecha en el cliente (sin paginación/scroll infinito en v1, ver plan).
 */
@Component({
  selector: 'app-conversation-list',
  standalone: true,
  imports: [TranslateModule, MatDialogModule],
  providers: [AppConfirmService],
  templateUrl: './conversation-list.component.html',
  styleUrls: ['./conversation-list.component.scss']
})
export class ConversationListComponent implements OnInit {
  @Input() activeConversationId: string | null = null;
  @Output() selectConversation = new EventEmitter<ConversationSummary>();
  @Output() newChat = new EventEmitter<void>();

  conversations: ConversationSummary[] = [];
  groupedConversations: ConversationGroup[] = [];
  loading = true;
  loadError = false;
  deleteError = false;

  constructor(
    private conversationService: AssistantConversationService,
    private confirmService: AppConfirmService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.refresh();
  }

  /**
   * @param silent si es true, no muestra el spinner de carga inicial — se usa
   * para refrescos en segundo plano (p.ej. tras cada respuesta del asistente)
   * para no hacer parpadear/desaparecer toda la sidebar en cada mensaje.
   */
  refresh(silent = false): void {
    if (!silent) {
      this.loading = true;
    }
    this.conversationService.list(0, PAGE_SIZE).subscribe({
      next: (response) => {
        this.conversations = response.conversations;
        this.groupedConversations = this.computeGroups(this.conversations);
        this.loading = false;
        this.loadError = false;
      },
      error: () => {
        // No se vacía this.conversations/groupedConversations: un refresco en
        // segundo plano fallido no debe borrar el historial ya visible.
        this.loading = false;
        this.loadError = true;
      }
    });
  }

  private computeGroups(conversations: ConversationSummary[]): ConversationGroup[] {
    const today = this.startOfDay(new Date());
    const yesterday = today - DAY_MS;
    const weekAgo = today - 6 * DAY_MS;

    const buckets: Record<'today' | 'yesterday' | 'thisWeek' | 'older', ConversationSummary[]> = {
      today: [], yesterday: [], thisWeek: [], older: []
    };

    for (const c of conversations) {
      const t = this.startOfDay(new Date(c.updatedAt));
      if (t === today) buckets.today.push(c);
      else if (t === yesterday) buckets.yesterday.push(c);
      else if (t >= weekAgo) buckets.thisWeek.push(c);
      else buckets.older.push(c);
    }

    return [
      { label: 'assistantPage.list.today', items: buckets.today },
      { label: 'assistantPage.list.yesterday', items: buckets.yesterday },
      { label: 'assistantPage.list.thisWeek', items: buckets.thisWeek },
      { label: 'assistantPage.list.older', items: buckets.older }
    ].filter(g => g.items.length > 0);
  }

  onSelect(item: ConversationSummary): void {
    this.selectConversation.emit(item);
  }

  onNewChat(): void {
    this.newChat.emit();
  }

  onDelete(item: ConversationSummary, event: MouseEvent): void {
    event.stopPropagation();
    this.confirmService.confirm({
      title: this.translate.instant('assistantPage.list.deleteTitle'),
      message: this.translate.instant('assistantPage.list.deleteMessage'),
      confirmText: this.translate.instant('assistantPage.list.deleteConfirm'),
      cancelText: this.translate.instant('assistantPage.list.deleteCancel')
    }).subscribe(confirmed => {
      if (!confirmed) return;
      this.conversationService.delete(item.id).subscribe({
        next: () => {
          this.conversations = this.conversations.filter(c => c.id !== item.id);
          this.groupedConversations = this.computeGroups(this.conversations);
          this.deleteError = false;
          if (this.activeConversationId === item.id) {
            this.newChat.emit();
          }
        },
        error: () => {
          this.deleteError = true;
        }
      });
    });
  }

  dismissDeleteError(): void {
    this.deleteError = false;
  }

  trackById(_index: number, item: ConversationSummary): string {
    return item.id;
  }

  private startOfDay(d: Date): number {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  }
}
